// HospitalityDialog.js
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { TimeSelect } from '../../ui/TimeSelect';
import { Badge } from '../../ui/badge';
import { toast } from 'sonner';
import { activityAPI, hospitalityAPI, marketingAPI } from '../../../services/api';
import { Pencil, AlertCircle, Calendar, User, Link2, Clock, Sparkles, Trash2, Mail, MessageSquare, CheckCircle2, Upload, Camera, DollarSign, FileText, Gift, Users, Star, Building2, Download, EyeOff } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { associationAPI } from '../../../services/api';
import api from '../../../services/api';

export const HospitalityDialog = ({ open, onOpenChange, accountId, onSuccess, associations = [] }) => {
  const [loading, setLoading] = useState(false);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [formData, setFormData] = useState({
    category: 'gift',
    short_summary: '',
    details: '',
    date: new Date().toISOString().split('T')[0],
    currency: 'USD',
    monetary_value: 0,
    recipient_association_id: '',
    approval_status: 'na',
  });
  const [receiptFile, setReceiptFile] = useState(null);
  const [photoFiles, setPhotoFiles] = useState([]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.short_summary) {
      toast.error('Summary is required');
      return;
    }

    setLoading(true);
    try {
      await hospitalityAPI.create({
        ...formData,
        crm_entity_id: accountId,
        date: formData.date ? `${formData.date}T12:00:00.000Z` : null,
        receipt_file_id: receiptFile?.id || null,
        photo_file_ids: photoFiles.map(f => f.id),
      });
      toast.success('Hospitality/Gift logged');
      onSuccess?.();
      onOpenChange(false);
      setFormData({
        category: 'gift',
        short_summary: '',
        details: '',
        date: new Date().toISOString().split('T')[0],
        currency: 'USD',
        monetary_value: 0,
        recipient_association_id: '',
        approval_status: 'na',
      });
      setReceiptFile(null);
      setPhotoFiles([]);
    } catch (error) {
      console.error('Failed to log hospitality:', error);
      toast.error('Failed to log entry');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e, type = 'photo') => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const isReceipt = type === 'receipt';
    isReceipt ? setUploadingReceipt(true) : setUploadingPhotos(true);
    
    try {
      const token = localStorage.getItem('token');
      const newFiles = [];

      for (let file of files) {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        formDataUpload.append('crm_entity_id', accountId);
        formDataUpload.append('description', isReceipt ? 'Receipt' : 'Photo');

        const response = await fetch(
          `${window.location.origin}/api/files/upload`,
          {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: formDataUpload,
          }
        );

        if (response.ok) {
          const fileData = await response.json();
          newFiles.push(fileData);
        }
      }

      if (isReceipt && newFiles.length > 0) {
        setReceiptFile(newFiles[0]); // Only keep first receipt
        toast.success('Receipt uploaded');
      } else {
        setPhotoFiles([...photoFiles, ...newFiles]);
        toast.success(`${newFiles.length} photo(s) uploaded`);
      }
    } catch (error) {
      console.error('Failed to upload files:', error);
      toast.error('Failed to upload files');
    } finally {
      isReceipt ? setUploadingReceipt(false) : setUploadingPhotos(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="hospitality-dialog">
        <DialogHeader>
          <DialogTitle>Log Hospitality / Gift</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Category</Label>
            <Select 
              value={formData.category} 
              onValueChange={(val) => setFormData({ ...formData, category: val })}
            >
              <SelectTrigger data-testid="hosp-category-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="gift">Gift</SelectItem>
                <SelectItem value="hospitality_dining">Dining</SelectItem>
                <SelectItem value="hospitality_experience">Experience</SelectItem>
                <SelectItem value="hospitality_travel">Travel</SelectItem>
                <SelectItem value="hospitality_meeting">Meeting</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="hosp-summary">Summary *</Label>
            <Input
              id="hosp-summary"
              data-testid="hosp-summary-input"
              value={formData.short_summary}
              onChange={(e) => setFormData({ ...formData, short_summary: e.target.value })}
              placeholder="e.g., Birthday gift, Dinner at Keeneland"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="hosp-date">Date</Label>
              <Input
                id="hosp-date"
                data-testid="hosp-date-input"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Currency</Label>
              <Select 
                value={formData.currency} 
                onValueChange={(val) => setFormData({ ...formData, currency: val })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="GBP">GBP</SelectItem>
                  <SelectItem value="AUD">AUD</SelectItem>
                  <SelectItem value="CAD">CAD</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="hosp-value">Monetary Value</Label>
            <Input
              id="hosp-value"
              data-testid="hosp-value-input"
              type="number"
              min="0"
              step="0.01"
              value={formData.monetary_value}
              onChange={(e) => setFormData({ ...formData, monetary_value: parseFloat(e.target.value) || 0 })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="hosp-details">Details</Label>
            <Textarea
              id="hosp-details"
              data-testid="hosp-details-input"
              value={formData.details}
              onChange={(e) => setFormData({ ...formData, details: e.target.value })}
              placeholder="Additional details..."
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label>Recipient (if different)</Label>
            <Select 
              value={formData.recipient_association_id || 'none'} 
              onValueChange={(val) => setFormData({ ...formData, recipient_association_id: val === 'none' ? '' : val })}
            >
              <SelectTrigger data-testid="recipient-select">
                <SelectValue placeholder="Select recipient" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Same as account</SelectItem>
                {associations.map(assoc => (
                  <SelectItem key={assoc.id} value={assoc.id}>
                    {assoc.entityName} - {assoc.relationship?.replace('_', ' ')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Approval Status</Label>
            <Select 
              value={formData.approval_status} 
              onValueChange={(val) => setFormData({ ...formData, approval_status: val })}
            >
              <SelectTrigger data-testid="approval-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
                <SelectItem value="na">N/A</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Receipt (optional)</Label>
            <div className="flex gap-2">
              <input
                type="file"
                id="hosp-receipt-upload"
                accept="image/*,application/pdf"
                onChange={(e) => handleFileUpload(e, 'receipt')}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => document.getElementById('hosp-receipt-upload').click()}
                disabled={uploadingReceipt}
              >
                <Upload className="w-4 h-4 mr-2" />
                {receiptFile ? 'Replace Receipt' : 'Upload Receipt'}
              </Button>
              
              {receiptFile && (
                <span className="text-xs text-muted-foreground flex items-center">
                  ✓ {receiptFile.filename}
                </span>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Photos (optional)</Label>
            <div className="flex gap-2">
              <input
                type="file"
                id="hosp-photo-upload"
                accept="image/*"
                multiple
                onChange={(e) => handleFileUpload(e, 'photo')}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => document.getElementById('hosp-photo-upload').click()}
                disabled={uploadingPhotos}
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Photos
              </Button>
              
              <input
                type="file"
                id="hosp-photo-camera"
                accept="image/*"
                capture="environment"
                onChange={(e) => handleFileUpload(e, 'photo')}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => document.getElementById('hosp-photo-camera').click()}
                disabled={uploadingPhotos}
              >
                <Camera className="w-4 h-4 mr-2" />
                Take Photo
              </Button>
            </div>
            {photoFiles.length > 0 && (
              <div className="text-xs text-muted-foreground mt-2">
                {photoFiles.length} photo(s) uploaded
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} data-testid="hosp-submit-btn">
              {loading ? 'Saving...' : 'Save Entry'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

